import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Folder, FolderRole } from '@/api';
import { ApiError } from '@/api/http';
import { useFolderContext, type FolderContextValue } from '@/app/folders';
import { cssVars } from '@/utils/css';
import { OutboxFolderEntry } from '@/features/outbox/OutboxFolderEntry';
import { viewSegmentOf } from '@/features/scheduling/route';
import { Menu, type MenuAnchor } from '@/features/context-menu';
import { useHandheld } from '@/hooks/useMediaQuery';
import type { FolderDialogAction } from './FolderDialog';
import { FolderDialog } from '@/app/lazyUi';
import {
  folderHref,
  folderMenuItems,
  openInNewTab,
  useMenuButtonToggle,
  useRowMenuTriggers,
  type FolderMenuHandlers,
} from './FolderMenu';
import { Icon, type IconName } from './Icon';
import { useFolderDrop } from '@/features/dnd/useFolderDrop';
import './folder-manage.css';

/**
 * Per-account folder tree.
 *
 * The backend returns a flat list with `parentId`; the tree is assembled here so
 * the API stays simple. Expansion state is local and keyed by folder id.
 */

const ROLE_ICONS: Record<FolderRole, IconName> = {
  inbox: 'inbox',
  drafts: 'draft',
  sent: 'send',
  archive: 'archive',
  junk: 'junk',
  trash: 'trash',
  // ADR 0076: the job folders, which link to their views (features/scheduling/route.ts).
  scheduled: 'clock',
  snoozed: 'bell',
  custom: 'folder',
};

export interface FolderTreeProps {
  folders: Folder[];
  selectedFolderId: string | undefined;
  onSelect: (folder: Folder) => void;
  /**
   * Failure of `GET /folders`. Optional: when it is omitted the tree reads the
   * surrounding `FolderProvider`, which is how the shell's instance gets it —
   * the shell renders `<FolderTree folders={…} />` and has no error to pass
   * (docs/plan/11 §9.1 assumes the tree can see `error`/`reload`).
   */
  error?: Error | undefined;
  /** Retry action; defaults to the provider's `reload()`. */
  onRetry?: (() => void) | undefined;
  /**
   * This tree is one mailbox's branch of a bigger tree (the sidebar, once more
   * than one mailbox is connected), so its rows are a `group` owned by the
   * mailbox's `treeitem` rather than a `tree` of their own — a `tree` inside a
   * `treeitem` is not a thing ARIA has.
   */
  nested?: boolean;
  /** How many levels sit above these rows; shifts both `aria-level` and the indent. */
  depthOffset?: number;
}

/**
 * The folder context when there is one, `undefined` when the tree is rendered
 * bare (component tests do exactly that). `useContext` still runs on every
 * render, so hook order is unconditional; only the provider-missing throw is
 * swallowed.
 */
function useOptionalFolderContext(): FolderContextValue | undefined {
  try {
    return useFolderContext();
  } catch {
    return undefined;
  }
}

/**
 * Why the folder list is empty, in the user's words.
 *
 * A `401` is deliberately absent: the session layer owns re-authentication and
 * a second "you are signed out" line in the sidebar is noise. A `503` is
 * deliberately *not* presented as a sign-out — the backend answers 503 exactly
 * so a datastore outage can be told apart from a dead session.
 */
function folderErrorCopy(error: Error): string | undefined {
  if (error instanceof ApiError) {
    if (error.isSessionExpired) return undefined;
    if (error.isOffline) return 'You’re offline, so folders couldn’t be loaded.';
    if (error.isServiceUnavailable) return 'Folders are temporarily unavailable, retrying…';
  }
  return 'Couldn’t load folders.';
}

interface TreeNode {
  folder: Folder;
  children: TreeNode[];
}

function buildTree(folders: Folder[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>();
  for (const folder of folders) nodes.set(folder.id, { folder, children: [] });

  const roots: TreeNode[] = [];
  for (const folder of folders) {
    const node = nodes.get(folder.id);
    if (!node) continue;
    const parent = folder.parentId ? nodes.get(folder.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  // Special folders first, in the order people expect; custom folders A-Z.
  const roleOrder: FolderRole[] = [
    'inbox',
    'drafts',
    'sent',
    'archive',
    'junk',
    'trash',
    'scheduled',
    'snoozed',
    'custom',
  ];
  const sort = (list: TreeNode[]): TreeNode[] => {
    list.sort((a, b) => {
      const byRole = roleOrder.indexOf(a.folder.role) - roleOrder.indexOf(b.folder.role);
      if (byRole !== 0) return byRole;
      return a.folder.name.localeCompare(b.folder.name);
    });
    for (const node of list) sort(node.children);
    return list;
  };
  return sort(roots);
}


export function FolderTree({
  folders,
  selectedFolderId,
  onSelect,
  error,
  onRetry,
  nested = false,
  depthOffset = 0,
}: FolderTreeProps) {
  const context = useOptionalFolderContext();
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = useState<FolderDialogAction | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const failure = error ?? context?.error;
  const retry = onRetry ?? context?.reload;
  const message = failure ? folderErrorCopy(failure) : undefined;

  const toggle = useCallback((folderId: string) => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  // Open only, never close: what a dragged message resting on a collapsed
  // parent does (#168). A toggle would shut a row opened a moment earlier.
  const expand = useCallback((folderId: string) => {
    setCollapsed((previous) => {
      if (!previous.has(folderId)) return previous;
      const next = new Set(previous);
      next.delete(folderId);
      return next;
    });
  }, []);

  const markRead = useMarkFolderRead(context, setStatus);
  const menu = useFolderMenu(context, setDialog, markRead, setStatus);

  return (
    <>
      {/*
        The failure line sits *outside* `role="tree"`: a tree may only own
        treeitems, so an alert parked among them is both invalid ARIA and, to a
        screen reader walking the tree, an unreachable row.
      */}
      {message !== undefined && (
        <div className="folder-tree__status" role="alert">
          <p className="muted">{message}</p>
          {retry && (
            <button type="button" className="btn btn--ghost" onClick={retry}>
              Retry
            </button>
          )}
        </div>
      )}
      {message === undefined && folders.length === 0 && (
        <p className="muted folder-tree__status">No folders</p>
      )}
      {/*
        Management is offered only when there is a provider to run it: the
        component is also rendered bare (component tests, and anywhere a plain
        list of folders is wanted), and a button that would throw on click is
        worse than no button.
      */}
      {context !== undefined && (
        <div className="folder-tree__group-actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setDialog({ kind: 'create', parent: undefined })}
          >
            <Icon name="plus" size={14} /> New folder
          </button>
        </div>
      )}
      <div
        className="folder-tree"
        // The folders pane for F6 (#205); focusable by script only, for when
        // no row is the open folder.
        {...(nested
          ? { role: 'group' }
          : { role: 'tree', 'aria-label': 'Folders', 'data-pane': 'folders', tabIndex: -1 })}
      >
        {tree.map((node) => (
          <Fragment key={node.folder.id}>
            <FolderRow
              node={node}
              depth={depthOffset}
              collapsed={collapsed}
              onToggle={toggle}
              onExpand={expand}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              context={context}
              menu={menu}
            />
            {/*
              The Outbox sits directly under Drafts (issue #201). It is not a
              server folder -- it draws nothing and asks for nothing -- but it
              is where a user looks for a message they pressed Send on, and
              Drafts is its right neighbour: "written, not sent" next to "sent,
              not gone". It renders nothing at all while the queue is empty.
            */}
            {node.folder.role === 'drafts' && <OutboxFolderEntry depth={depthOffset} />}
          </Fragment>
        ))}
      </div>
      {status !== null && (
        <p className="muted folder-tree__status" role="status">
          {status}
        </p>
      )}
      {menu.element}
      {dialog !== null && (
        <FolderDialog action={dialog} onClose={() => setDialog(null)} onDone={setStatus} />
      )}
    </>
  );
}

/**
 * Mark a folder read, reporting through `report`.
 *
 * Marking a folder read is not a dialog: nothing is destroyed and there is
 * nothing to ask. It IS slow on a big mailbox -- one UID SEARCH and a UID
 * STORE per thousand messages -- so the row says so while it runs, and
 * nothing is zeroed optimistically: the badge keeps its old number until the
 * server has said how many it actually flagged. A run that stopped half way
 * answers 200 with `partial`, and that is what the status line reports,
 * because "done" over a folder that is still half unread is the one answer
 * the UI must not give.
 */
function useMarkFolderRead(
  context: FolderContextValue | undefined,
  report: (message: string | null) => void,
): (folder: Folder) => void {
  return useCallback(
    (folder: Folder) => {
      if (context === undefined) return;
      report(null);
      void context
        .markFolderRead(folder.id)
        .then((result) => {
          report(
            result.partial === true
              ? `Marked ${result.updatedCount.toLocaleString()} messages in ${folder.name} read; some could not be updated. Try again to finish.`
              : result.updatedCount === 0
                ? `Nothing was unread in ${folder.name}.`
                : `Marked ${result.updatedCount.toLocaleString()} messages in ${folder.name} read.`,
          );
        })
        .catch((cause: unknown) => {
          report(
            cause instanceof ApiError ? cause.message : `${folder.name} could not be marked read.`,
          );
        });
    },
    [context, report],
  );
}


/** Opening the one folder menu of a tree, from any of its ways in. */
interface FolderMenuControl {
  /** The folder whose menu is open, if any. */
  openFor: string | undefined;
  open: (folder: Folder, anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => void;
  /** The ⋯ buttons' toggle (see `useMenuButtonToggle`). */
  toggle: ReturnType<typeof useMenuButtonToggle>;
  /** The open menu, or null: rendered once per tree, portalled out of it. */
  element: ReactNode;
}

/**
 * One menu per tree, whichever row asked for it. The items are
 * `folderMenuItems` (FolderMenu.tsx) — the same list for ⋯, right-click, the
 * keyboard and a long-press — and the container is the message list's `Menu`:
 * portalled to <body> and placed in viewport coordinates, so the sidebar's
 * scroll container cannot clip it (#286), flipped and clamped at the viewport
 * edges, and a bottom sheet on handheld, where the tree lives in the drawer.
 */
function useFolderMenu(
  context: FolderContextValue | undefined,
  onAction: (action: FolderDialogAction) => void,
  markRead: (folder: Folder) => void,
  report: (message: string | null) => void,
): FolderMenuControl {
  const handheld = useHandheld();
  const variant = handheld ? 'sheet' : 'popover';
  const [request, setRequest] = useState<{
    folder: Folder;
    anchor: MenuAnchor;
    returnFocusTo: HTMLElement | null;
  } | null>(null);

  const open = useCallback((folder: Folder, anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => {
    setRequest({ folder, anchor, returnFocusTo });
  }, []);
  const toggle = useMenuButtonToggle();
  const noteClose = toggle.noteClose;
  const close = useCallback(() => {
    noteClose();
    setRequest(null);
  }, [noteClose]);

  const handlers = useMemo<FolderMenuHandlers>(
    () => ({
      openInNewTab: (folder) => openInNewTab(folderHref(folder)),
      refresh: (folder) => {
        if (!context) return;
        report(null);
        void context
          .refreshFolder(folder)
          .then(() => report(`${folder.name} is up to date.`))
          .catch(() => report(`${folder.name} could not be refreshed.`));
      },
      markRead,
      empty: (folder) => onAction({ kind: 'empty', folder }),
      createInside: (folder) => onAction({ kind: 'create', parent: folder }),
      rename: (folder) => onAction({ kind: 'rename', folder }),
      remove: (folder) => onAction({ kind: 'delete', folder }),
    }),
    [context, markRead, onAction, report],
  );

  // The row the menu belongs to can be refetched away (a rename elsewhere, a
  // live update) while it is open; the menu acts on the folder it was opened
  // for, which is what the person pointed at.
  const element =
    request !== null && context !== undefined ? (
      <Menu
        label={`Folder actions for ${request.folder.name}`}
        items={folderMenuItems(request.folder, handlers, {
          variant,
          canModify: context.protectedBy(request.folder) === undefined,
        })}
        anchor={request.anchor}
        returnFocusTo={request.returnFocusTo}
        onClose={close}
        variant={variant}
      />
    ) : null;

  return { openFor: request?.folder.id, open, toggle, element };
}

/**
 * "Mark all as read" and "Empty folder…" for ONE folder, as a self-contained
 * ⋮ button with its own dialog and result line — for the mail list's header,
 * so the folder being looked at can be acted on without opening the handheld
 * drawer to find its row. The items are the `content` subset of the tree's
 * folder menu. Renders nothing outside a `FolderProvider`, or for a folder
 * that holds no mail of its own.
 */
export function FolderActionsButton({ folder }: { folder: Folder }) {
  const context = useOptionalFolderContext();
  const handheld = useHandheld();
  const [opened, setOpened] = useState<{ anchor: DOMRect; button: HTMLElement } | null>(null);
  const toggle = useMenuButtonToggle();
  const [dialog, setDialog] = useState<FolderDialogAction | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const markRead = useMarkFolderRead(context, setStatus);

  // The result line is a transient card under the button: the header has no
  // room for a sentence, and a count that stays forever goes stale.
  useEffect(() => {
    if (status === null) return undefined;
    const timer = window.setTimeout(() => setStatus(null), 8000);
    return () => window.clearTimeout(timer);
  }, [status]);

  if (context === undefined || !folder.selectable) return null;
  const marking = context.markingReadId === folder.id;
  const variant = handheld ? 'sheet' : 'popover';
  const noop = (): void => undefined;

  return (
    <span className="folder-actions">
      <button
        type="button"
        className="btn btn--icon"
        aria-haspopup="menu"
        aria-expanded={opened !== null}
        aria-label={`Actions for ${folder.name}`}
        title={`Actions for ${folder.name}`}
        onPointerDown={toggle.onPointerDown}
        onClick={(event) => {
          if (toggle.consumeClick()) return;
          const button = event.currentTarget;
          setOpened({ anchor: button.getBoundingClientRect(), button });
        }}
      >
        <Icon name="more" />
      </button>
      {opened !== null && (
        <Menu
          label={`Actions for ${folder.name}`}
          items={folderMenuItems(
            folder,
            {
              openInNewTab: noop,
              refresh: noop,
              markRead,
              empty: (target) => setDialog({ kind: 'empty', folder: target }),
              createInside: noop,
              rename: noop,
              remove: noop,
            },
            { scope: 'content', variant, canModify: false },
          )}
          anchor={opened.anchor}
          returnFocusTo={opened.button}
          onClose={() => {
            toggle.noteClose();
            setOpened(null);
          }}
          variant={variant}
        />
      )}
      {(marking || status !== null) && (
        <p className="folder-actions__status card" role="status">
          {marking ? 'Marking read…' : status}
        </p>
      )}
      {dialog !== null && (
        <FolderDialog action={dialog} onClose={() => setDialog(null)} onDone={setStatus} />
      )}
    </span>
  );
}

interface FolderRowProps {
  node: TreeNode;
  depth: number;
  collapsed: Set<string>;
  onToggle: (folderId: string) => void;
  /** Opens a collapsed row (never closes one): the drag's hover-to-open. */
  onExpand: (folderId: string) => void;
  selectedFolderId: string | undefined;
  onSelect: (folder: Folder) => void;
  /** Absent when the tree is rendered without a provider; no actions then. */
  context: FolderContextValue | undefined;
  menu: FolderMenuControl;
}

function FolderRow({
  node,
  depth,
  collapsed,
  onToggle,
  onExpand,
  selectedFolderId,
  onSelect,
  context,
  menu,
}: FolderRowProps) {
  const { folder, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = hasChildren && !collapsed.has(folder.id);
  // The job folders open their views (/mail/scheduled, /mail/snoozed), whose
  // segment is what the shell reports as the selected folder there.
  const isSelected =
    folder.id === selectedFolderId ||
    (selectedFolderId !== undefined && selectedFolderId === viewSegmentOf(folder));
  const marking = context?.markingReadId === folder.id;
  const menuOpen = menu.openFor === folder.id;
  // A message row dragged over this one (#168): a drop target when it would
  // take the drag, and a collapsed parent opens after a short rest.
  const drop = useFolderDrop(folder, {
    collapsed: hasChildren && !isExpanded,
    onExpand: useCallback(() => onExpand(folder.id), [folder.id, onExpand]),
  });

  // Right-click, Shift+F10 / ContextMenu and long-press, all onto the same
  // menu as the ⋯ button. Only with a provider: without one there are no
  // actions to offer, and the browser's own menu is left alone.
  const rowRef = useRef<HTMLDivElement>(null);
  const openMenu = menu.open;
  useRowMenuTriggers(
    rowRef,
    context !== undefined,
    useCallback(
      (anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => openMenu(folder, anchor, returnFocusTo),
      [folder, openMenu],
    ),
  );

  return (
    <>
      <div
        ref={rowRef}
        role="treeitem"
        // Named for the folder (#205): from its content it read "Collapse
        // Inbox Inbox 26", the twisty's label and the button's run together.
        aria-label={folder.unreadCount > 0 ? `${folder.name}, ${folder.unreadCount} unread` : folder.name}
        aria-selected={isSelected}
        {...(hasChildren ? { 'aria-expanded': isExpanded } : {})}
        aria-level={depth + 1}
      >
        <div
          className={
            'folder-tree__row' +
            (isSelected ? ' is-active' : '') +
            (menuOpen ? ' has-menu' : '') +
            (drop.isTarget ? ' is-drop-target' : '')
          }
          style={cssVars({ '--depth': depth })}
          {...drop.props}
        >
          {hasChildren ? (
            <button
              type="button"
              className="folder-tree__twisty"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
              onClick={() => onToggle(folder.id)}
            >
              <Icon name="chevron-right" size={14} />
            </button>
          ) : (
            <span className="folder-tree__twisty folder-tree__twisty--placeholder" />
          )}
          <button
            type="button"
            className="folder-tree__main"
            onClick={() => folder.selectable && onSelect(folder)}
            disabled={!folder.selectable}
          >
            <Icon name={ROLE_ICONS[folder.role]} size={16} />
            <span className="folder-tree__label">{folder.name}</span>
            {folder.unreadCount > 0 && (
              <span className={isSelected ? 'badge badge--accent' : 'badge'}>{folder.unreadCount}</span>
            )}
          </button>
          {marking && <span className="folder-tree__busy">Marking read…</span>}
          {context !== undefined && (
            <button
              type="button"
              className="folder-tree__menu-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Folder actions for ${folder.name}`}
              // A press on ⋯ while its menu is open is a close: the menu's own
              // outside-press rule has shut it before this click lands, so the
              // click must not open it again.
              onPointerDown={menu.toggle.onPointerDown}
              onClick={(event) => {
                if (menu.toggle.consumeClick()) return;
                const button = event.currentTarget;
                menu.open(folder, button.getBoundingClientRect(), button);
              }}
            >
              <Icon name="more" size={14} />
            </button>
          )}
        </div>
      </div>
      {isExpanded &&
        children.map((child) => (
          <FolderRow
            key={child.folder.id}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            onExpand={onExpand}
            selectedFolderId={selectedFolderId}
            onSelect={onSelect}
            context={context}
            menu={menu}
          />
        ))}
    </>
  );
}
